import { CanAccess } from '@modules/common/components/CanAccess';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useInviteUsersByLink } from '@modules/users/hooks';
import { UserRowRoleType } from '@modules/users/store/types';
import { InviteUserRoleType } from '@modules/users/types';
import { trackUserManagementEvents } from '@modules/workspaceSettings/utils';
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import { LoadingButton } from '@mui/lab';
import { Grid, Stack, Typography } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { useState } from 'react';
import { InviteUsersRoleSelect } from './InviteUsersRoleSelect';

interface InviteUsersByLinkProps {
  shouldInviteAdminsOnly?: boolean;
}

export const InviteUsersByLink = ({
  shouldInviteAdminsOnly,
}: InviteUsersByLinkProps) => {
  const [selectedRole, setSelectedRole] = useState<InviteUserRoleType>(
    shouldInviteAdminsOnly ? UserRowRoleType.Admin : UserRowRoleType.Member,
  );

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const canAccessWorkspaceSettings = useIsAuthorized({
    resource: PermissionResource.WorkspaceSettings,
    operation: PermissionOperation.Write,
  });

  const { inviteUsersByLink, isLoading: isInviteRequestInProgress } =
    useInviteUsersByLink({
      onError: () => {
        showErrorNotification({
          contentComponent: 'Failed to generated invite link',
          props: {},
        });
      },
    });

  const handleChangeRole = (role: InviteUserRoleType) => {
    setSelectedRole(role);
  };

  const onInviteUsersByLink = async () => {
    try {
      if ('ClipboardItem' in window) {
        const text = new ClipboardItem({
          'text/plain': inviteUsersByLink(selectedRole).then(
            (text) => new Blob([text], { type: 'text/plain' }),
          ),
        });
        await navigator.clipboard.write([text]);
      }
      showSuccessNotification({
        contentComponent: 'Invite link copied to Clipboard',
        props: {},
      });
      trackUserManagementEvents({
        eventName:
          'User Management - Invite Users: Clicked On Copy Link Button',
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to copy invite link to clipboard',
        props: {},
      });
    }
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <LinkRoundedIcon />
        <Typography variant="subtitle1">Invite by link</Typography>
      </Stack>
      <Grid container alignItems="flex-start" columnSpacing={2}>
        <Grid item xs={canAccessWorkspaceSettings ? 7 : 9.5} ml={1.75}>
          <Typography component="span" variant="body2">
            Coworkers signing up with this link will join Enterpret{' '}
          </Typography>
          <Typography component="span" variant="subtitle2">
            {selectedRole === UserRowRoleType.Admin
              ? 'as Admin.'
              : 'as Member.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            (The invite link is valid for 14 days)
          </Typography>
        </Grid>
        {canAccessWorkspaceSettings && (
          <Grid item xs={2.5}>
            <InviteUsersRoleSelect
              selectedRole={selectedRole}
              onChange={handleChangeRole}
              isDisabled={shouldInviteAdminsOnly}
              selectedEmails={[]}
            />
          </Grid>
        )}
        <Grid item xs={2}>
          <CanAccess
            resource={PermissionResource.Invite}
            operation={PermissionOperation.Write}
          >
            <LoadingButton
              size="small"
              fullWidth
              variant="contained"
              startIcon={<InsertLinkRoundedIcon />}
              loading={isInviteRequestInProgress}
              onClick={onInviteUsersByLink}
              sx={{
                py: 0.9,
              }}
            >
              Copy link
            </LoadingButton>
          </CanAccess>
        </Grid>
      </Grid>
    </Stack>
  );
};
